import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, MagnifyingGlass, Sparkle, Compass, Buildings,
  Users,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/context/I18nContext";

// ---------------------------------------------------------------------------
// Search — manual discovery entry point.
//
// Responsibility (strict): CREATE a new discovery session using free-text
// goal + optional filters, then navigate to SessionDetail to see results.
//
// This page never shows results. It never lists past searches. Those are
// SessionDetail's and History's jobs respectively.
// ---------------------------------------------------------------------------
export default function Search() {
  const { locale } = useI18n();
  const nav = useNavigate();
  const t = (tr, en) => (locale === "en" ? en : tr);

  const [goal, setGoal] = useState("");
  const [location, setLocation] = useState("");
  const [target, setTarget] = useState("");   // e.g. "boutique gift shops"
  const [starting, setStarting] = useState(false);

  const composedGoal = () => {
    // Compose the natural-language prompt from the fields. Free-text goal
    // wins; filters are appended only when they add real information.
    const parts = [];
    if (goal.trim()) parts.push(goal.trim());
    if (target.trim()) parts.push(t(`Hedef: ${target.trim()}`, `Target: ${target.trim()}`));
    if (location.trim()) parts.push(t(`Bölge: ${location.trim()}`, `Location: ${location.trim()}`));
    return parts.join(". ");
  };

  const start = async (overrideGoal) => {
    if (!location.trim()) {
      toast.error("Arama yapabilmek için şehir seçin.");
      return;
    }
    const g = (overrideGoal ?? composedGoal()).trim();
    if (g.length < 3) return;
    setStarting(true);
    try {
      const { data } = await api.post("/agent/run", { goal: g, language: locale });
      toast.success(t("Arama başlatıldı — sonuçlar hazırlanıyor…", "Search started — preparing results…"));
      // Redirect straight to SessionDetail so the user sees results/state there.
      // SessionDetail auto-polls via /history/sessions/{id} on load.
      nav(`/gecmis/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Başlatılamadı", "Could not start"));
    } finally {
      setStarting(false);
    }
  };

  const pickExample = (text) => setGoal(text);

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-8">
      <div>
        <div className="acas-eyebrow mb-2 flex items-center gap-1.5">
          <MagnifyingGlass size={11} weight="fill" className="text-primary" />
          {t("Manuel Keşif", "Manual Discovery")}
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="search-title">
          {t("Yeni bir arama başlat", "Start a new search")}
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
          {t(
            "Aradığınız fırsatı kendi kelimelerinizle anlatın. Next io uygun kaynaklara kendisi karar verir, sonuçları değerlendirir ve size sıralı bir liste sunar. Sonuçlar açılan sayfada görünecek.",
            "Describe what you're looking for in your own words. Next io decides the sources, evaluates results, and hands you a ranked list. Results open on the next page."
          )}
        </p>
      </div>

      {/* Prompt card */}
      <div className="rounded-2xl border border-primary/25 bg-primary/[0.03] p-1.5 acas-beam" data-testid="search-prompt-box">
        <div className="rounded-xl bg-card p-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
              {t("Hedefinizi yazın", "Describe your goal")}
            </label>
            <Textarea
              data-testid="search-goal-input"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t(
                "Örn: El yapımı seramik takılar için İstanbul'daki butik hediyelik dükkanlarını bul.",
                "e.g., Find boutique gift shops in Istanbul for my handmade ceramic jewelry."
              )}
              rows={3}
              className="mt-1.5 border border-border/60 bg-card focus-visible:ring-1 resize-none text-base leading-relaxed"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); start(); }
              }}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                {t("Şehir", "City")}
              </label>
              <Input
                data-testid="search-location-input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                aria-required="true"
                placeholder={t("İstanbul, Kadıköy, Türkiye…", "Istanbul, Istanbul, Turkey…")}
                className="mt-1.5"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                {t("Hedef fırsat türü (opsiyonel)", "Opportunity type (optional)")}
              </label>
              <Input
                data-testid="search-target-input"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder={t("restoranlar, butik dükkanlar, satış noktaları…", "restaurants, boutique shops, sales points…")}
                className="mt-1.5"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-border/60">
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" className="text-primary" />
              {t(
                "Kaynakları seçmenize gerek yok. Next io karar verir.",
                "You don't pick the sources. Next io decides."
              )}
            </div>
            <Button
              data-testid="search-run-btn"
              onClick={() => start()}
              disabled={starting || composedGoal().length < 3}
              size="lg"
              className="gap-2"
            >
              {starting ? t("Başlatılıyor…", "Starting…") : t("Aramayı Başlat", "Start search")}
              <ArrowRight size={16} weight="bold" />
            </Button>
          </div>
        </div>
      </div>

      {/* Example prompts */}
      <div className="rounded-lg border border-dashed border-border/60 p-5" data-testid="search-examples">
        <div className="acas-eyebrow mb-3 flex items-center gap-1.5">
          <Compass size={11} weight="fill" />
          {t("Örnek aramalar", "Example searches")}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {(locale === "en" ? [
            { icon: Users,    text: "Find retail buyers for our handmade ceramic jewelry in Istanbul." },
            { icon: Buildings,text: "Find B2B software resellers who can stock our Turkish SaaS." },
            { icon: Users,    text: "Find restaurants that could use our wholesale olive oil." },
            { icon: Buildings,text: "Find sales points for our specialty food range." },
          ] : [
            { icon: Users,    text: "El yapımı seramik takılar için İstanbul'daki butik dükkanları bul." },
            { icon: Buildings,text: "Türk SaaS ürünümüzü satabilecek bayi ve satış noktaları bul." },
            { icon: Users,    text: "Toptan zeytinyağımızı kullanabilecek restoranları bul." },
            { icon: Buildings,text: "Özel gıda ürünlerimiz için satış noktaları bul." },
          ]).map((ex, i) => {
            const Icon = ex.icon;
            return (
              <button
                key={i}
                type="button"
                onClick={() => pickExample(ex.text)}
                data-testid={`search-example-${i}`}
                className="text-left rounded-md border border-border/60 bg-card p-3 hover:border-primary/40 hover:bg-primary/[0.03] transition flex items-start gap-2.5"
              >
                <div className="w-8 h-8 rounded-md bg-primary/10 text-primary grid place-items-center flex-shrink-0">
                  <Icon size={15} weight="duotone" />
                </div>
                <span className="text-sm leading-relaxed">{ex.text}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
